'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { ReactNode } from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';

interface AnimatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  icon?: ReactNode;
}

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
  '2xl': 'max-w-3xl',
};

export function AnimatedModal({ isOpen, onClose, title, subtitle, children, size = 'lg', icon }: AnimatedModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex min-h-0 items-start justify-center overflow-y-auto overscroll-y-contain p-3 sm:items-center sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className={`relative my-auto flex max-h-[min(92dvh,calc(100dvh-1.5rem))] w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl ${sizes[size]}`}
          >
            {/* Decorative gradient bar */}
            <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
            
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                {icon && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: 'spring' }}
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-indigo-500/25 flex-shrink-0"
                  >
                    <span className="text-white text-sm sm:text-base">{icon}</span>
                  </motion.div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm sm:text-base font-bold text-gray-900 truncate">{title}</h2>
                  {subtitle && (
                    <p className="text-xs text-gray-500 truncate">{subtitle}</p>
                  )}
                </div>
              </div>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all flex-shrink-0 ml-2"
              >
                <X size={18} />
              </motion.button>
            </div>
            
            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Confirm Dialog Component
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  showCancel?: boolean;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  showCancel = true,
  variant,
}: ConfirmDialogProps) {
  // Use variant if provided, otherwise use type
  const dialogType = variant || type;
  
  const colors = {
    danger: {
      gradient: 'from-red-500 to-rose-600',
      button: 'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
      shadow: 'shadow-red-500/25',
      bg: 'bg-red-50',
      text: 'text-red-600',
    },
    warning: {
      gradient: 'from-amber-500 to-orange-600',
      button: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
      shadow: 'shadow-amber-500/25',
      bg: 'bg-amber-50',
      text: 'text-amber-600',
    },
    info: {
      gradient: 'from-blue-500 to-indigo-600',
      button: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
      shadow: 'shadow-blue-500/25',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
    },
    success: {
      gradient: 'from-emerald-500 to-teal-600',
      button: 'from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
      shadow: 'shadow-emerald-500/25',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
    },
  };

  const config = colors[dialogType as keyof typeof colors] || colors.danger;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex min-h-0 items-start justify-center overflow-y-auto overscroll-y-contain p-3 sm:items-center sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="relative my-auto w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl"
          >
            <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
            
            <div className="p-5 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
                className={`w-12 h-12 mx-auto mb-3 rounded-xl ${config.bg} flex items-center justify-center`}
              >
                {dialogType === 'success' ? (
                  <CheckCircle size={24} className={config.text} />
                ) : (
                  <AlertTriangle size={24} className={config.text} />
                )}
              </motion.div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
              <p className="text-sm text-gray-500 mb-5">{message}</p>
              
              <div className={`flex gap-2 ${!showCancel ? 'justify-center' : ''}`}>
                {showCancel && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="flex-1 py-2 px-3 rounded-lg font-medium text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    {cancelText}
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { onConfirm(); onClose(); }}
                  className={`${showCancel ? 'flex-1' : 'px-6'} py-2 px-3 rounded-lg font-medium text-sm text-white bg-gradient-to-r ${config.button} ${config.shadow} shadow-md transition-all`}
                >
                  {confirmText}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
